import React, { useState } from 'react';
import api from '../../services/api';
import { toast } from 'sonner';
import { gstStatusMeta, effectiveFilingStatus } from '../../lib/gstStatusCatalog';
import {
  Loader2, FileText, ChevronDown, ChevronRight, ShieldCheck, ShieldAlert,
  ShieldQuestion, IndianRupee, CheckCircle2, Gift, MapPin, Send, ExternalLink,
  CalendarClock, Building2, Upload, Download, X, RefreshCw,
} from 'lucide-react';

const INR = (n) =>
  new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 })
    .format(Number(n || 0));
const fmtDate = (d) =>
  d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

// ─── verification badge ──────────────────────────────────────────────────────
const VERIF = {
  verified: { cls: 'bg-emerald-500/20 text-emerald-300 border-emerald-500/40', label: 'Verified', Icon: ShieldCheck },
  disputed: { cls: 'bg-red-500/20 text-red-300 border-red-500/40', label: 'Disputed', Icon: ShieldAlert },
  pending: { cls: 'bg-amber-500/20 text-amber-300 border-amber-500/40', label: 'Verification pending', Icon: ShieldQuestion },
};
const VerificationBadge = ({ status }) => {
  const c = VERIF[status] || VERIF.pending;
  const { Icon } = c;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium border ${c.cls}`}>
      <Icon className="w-3 h-3" /> {c.label}
    </span>
  );
};

// ─── filing status → client-friendly label (single source of truth) ─────────
const filingStatusMeta = (s) => {
  const m = gstStatusMeta(s);
  return { cls: m.cls, label: m.label, description: m.description };
};

const PAY = {
  paid: { cls: 'bg-emerald-500/20 text-emerald-300', label: 'Paid' },
  paid_offline_approved: { cls: 'bg-emerald-500/20 text-emerald-300', label: 'Paid (offline)' },
  complimentary: { cls: 'bg-teal-500/20 text-teal-300', label: 'Complimentary' },
  covered_until_date: { cls: 'bg-emerald-500/20 text-emerald-300', label: 'Covered' },
  partly_paid: { cls: 'bg-amber-500/20 text-amber-300', label: 'Partly paid' },
  payment_link_generated: { cls: 'bg-blue-500/20 text-blue-300', label: 'Payment link ready' },
  offline_pending_approval: { cls: 'bg-amber-500/20 text-amber-300', label: 'Pending approval' },
  invoice_issued: { cls: 'bg-blue-500/20 text-blue-300', label: 'Invoice issued' },
  overdue: { cls: 'bg-red-500/20 text-red-300', label: 'Overdue' },
  expired: { cls: 'bg-red-500/20 text-red-300', label: 'Expired' },
  refunded: { cls: 'bg-slate-600/30 text-slate-400', label: 'Refunded' },
  waived: { cls: 'bg-teal-500/20 text-teal-300', label: 'Waived' },
  unpaid: { cls: 'bg-red-500/20 text-red-300', label: 'Unpaid' },
  not_set: { cls: 'bg-slate-600/30 text-slate-400', label: 'Not set' },
};
const PayBadge = ({ status }) => {
  const c = PAY[status] || PAY.not_set;
  return <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium ${c.cls}`}>{c.label}</span>;
};

const SCHEME_LABEL = { monthly: 'Monthly', iff_qrmp: 'QRMP (IFF)', not_applicable: 'N/A' };
const isQrmpReg = (r) =>
  r.taxpayer_profile === 'qrmp' || r.filing_frequency === 'quarterly' || r.gstr1_return_type === 'iff_qrmp';

// derive a quarter label + FY from a filing
const quarterOf = (f) => {
  const m = f.filing_month || (f.filing_period && /^\d{4}-\d{2}$/.test(f.filing_period) ? f.filing_period : null);
  if (m) {
    const [y, mm] = m.split('-').map(Number);
    const q = mm >= 4 && mm <= 6 ? 'Q1' : mm >= 7 && mm <= 9 ? 'Q2' : mm >= 10 && mm <= 12 ? 'Q3' : 'Q4';
    const fyStart = mm >= 4 ? y : y - 1;
    const fy = `${fyStart}-${String((fyStart + 1) % 100).padStart(2, '0')}`;
    return { key: `${fy} ${q}`, fy, q };
  }
  if (f.filing_period && f.filing_period.includes('Q')) return { key: f.filing_period, fy: f.financial_year, q: '' };
  return { key: f.financial_year || 'Annual', fy: f.financial_year, q: '' };
};

const FilingRow = ({ f, onOpenUpload, onDownload, onDownloadVersion, uploadingId, onGstr1Decision, decidingId, onNilConfirm }) => {
  const meta = filingStatusMeta(effectiveFilingStatus(f));
  const docs = Array.isArray(f.documents) ? f.documents : [];
  const busy = uploadingId === f.id;
  const deciding = decidingId === f.id;
  const [showDocs, setShowDocs] = useState(false);
  const awaitingConfirm = (f.form_type === 'GSTR-1' || f.form_type === 'CMP-08') && f.status === 'client_confirmation_pending';
  const nilFiled = ['nil_filed', 'filed', 'filed_complete', 'complete'].includes(f.status);
  const nilConfirmedByClient = f.status === 'nil_confirmed_by_client'
    || f.nil_filing?.selected_by_role === 'client' || f.nil_filing?.client_confirmation === true;
  const nilPending = !!f.nil_filing?.selected && !nilFiled && !nilConfirmedByClient;
  const versions = (f.gstr1_versions || []);
  const curVer = versions.find((v) => v.is_current);
  const olderVers = versions.filter((v) => !v.is_current);
  const requested = (f.checklist || []).filter((c) => c.requested_later && !docs.some((d) => d.document_type === c.code));
  const reqRemark = requested.find((r) => r.request_remark)?.request_remark;
  const hasFiles = docs.length > 0 || olderVers.length > 0;
  return (
    <tr className="border-t border-emerald-900/20 hover:bg-emerald-500/[0.03] transition-colors align-top" data-testid={`client-filing-row-${f.id}`}>
      <td className="px-3 py-3 text-white font-medium whitespace-nowrap">{f.form_type}
        {requested.length > 0 && (
          <div className="mt-1 text-[10px] text-amber-300 bg-amber-500/10 border border-amber-500/30 rounded px-1.5 py-1 max-w-[220px] whitespace-normal" data-testid={`client-docs-requested-${f.id}`}>
            <b>CA requested:</b> {requested.map((r) => r.label).join(', ')}{reqRemark ? ` — "${reqRemark}"` : ''}
          </div>
        )}
      </td>
      <td className="px-3 py-3 text-slate-300 whitespace-nowrap">{f.filing_period}{f.is_nil || f.nil_filing?.selected ? ' · Nil' : ''}</td>
      <td className="px-3 py-3 text-slate-400 whitespace-nowrap">{fmtDate(f.due_date)}</td>
      <td className="px-3 py-3">
        {!curVer && !hasFiles ? <span className="text-slate-600">—</span> : (
          <div className="space-y-1.5">
            <div className="flex flex-wrap items-center gap-1.5">
              {curVer && (
                <button onClick={() => onDownloadVersion(f.id, curVer)}
                        data-testid={`client-gstr1-current-${f.id}`}
                        className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded-md bg-blue-500/10 border border-blue-500/30 text-blue-300 hover:bg-blue-500/20 transition-colors">
                  <Download className="w-3 h-3 flex-shrink-0" /> Worksheet V{curVer.version_number}
                </button>
              )}
              {hasFiles && (
                <button onClick={() => setShowDocs((v) => !v)}
                        className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded-md bg-emerald-500/10 border border-emerald-500/30 text-emerald-300 hover:bg-emerald-500/20 transition-colors">
                  <FileText className="w-3 h-3 flex-shrink-0" /> {docs.length + olderVers.length} file{docs.length + olderVers.length > 1 ? 's' : ''}
                  {showDocs ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
                </button>
              )}
            </div>
            {showDocs && hasFiles && (
              <div className="flex flex-col gap-1 border-l border-emerald-900/30 ml-1 pl-2">
                {docs.map((d) => (
                  <button key={d.id} onClick={() => onDownload(f.id, d)}
                          data-testid={`client-doc-download-${d.id}`}
                          className="inline-flex items-center gap-1 text-[11px] text-slate-300 hover:text-emerald-200 text-left">
                    <Download className="w-3 h-3 flex-shrink-0" /> <span className="truncate max-w-[180px]">{d.document_name || d.file_name}</span>
                  </button>
                ))}
                {olderVers.map((v) => (
                  <button key={v.document_id} onClick={() => onDownloadVersion(f.id, v)}
                          data-testid={`client-gstr1-version-${f.id}-${v.version_number}`}
                          className="inline-flex items-center gap-1 text-[11px] text-slate-500 hover:text-slate-300 text-left">
                    <Download className="w-3 h-3 flex-shrink-0" /> GSTR-1 V{v.version_number} (older)
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </td>
      <td className="px-3 py-3 whitespace-nowrap">
        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium ${meta.cls}`}>{meta.label}</span>
      </td>
      <td className="px-3 py-3 text-right">
        {nilPending ? (
          <div className="flex flex-col items-end gap-1" data-testid={`client-nil-confirm-block-${f.id}`}>
            <span className="text-[10px] text-amber-300 whitespace-nowrap">Your CA marked this NIL</span>
            <div className="flex items-center justify-end gap-1">
              <button onClick={() => onNilConfirm(f, true)} disabled={deciding}
                      data-testid={`client-nil-confirm-${f.id}`}
                      className="text-[12px] px-2 py-1 rounded bg-emerald-500 hover:bg-emerald-400 text-black disabled:opacity-50 whitespace-nowrap">
                {deciding ? '...' : 'Confirm NIL'}
              </button>
              <button onClick={() => onNilConfirm(f, false)} disabled={deciding}
                      data-testid={`client-nil-had-txn-${f.id}`}
                      className="text-[12px] px-2 py-1 rounded border border-amber-500/50 text-amber-300 hover:bg-amber-500/10 disabled:opacity-50 whitespace-nowrap">
                I had transactions
              </button>
            </div>
          </div>
        ) : nilConfirmedByClient && !nilFiled ? (
          <span className="inline-flex items-center gap-1 text-[11px] text-emerald-300 whitespace-nowrap" data-testid={`client-nil-confirmed-${f.id}`}>
            <CheckCircle2 className="w-3 h-3" /> NIL confirmed
          </span>
        ) : awaitingConfirm ? (
          <div className="flex flex-col items-end gap-1">
            {curVer && (
              <button onClick={() => onDownloadVersion(f.id, curVer)} data-testid={`client-gstr1-review-${f.id}`}
                      className="text-[11px] text-blue-300 hover:text-blue-200 inline-flex items-center gap-1 whitespace-nowrap">
                <Download className="w-3 h-3" /> Review worksheet
              </button>
            )}
            <div className="flex items-center justify-end gap-1">
              <button onClick={() => onGstr1Decision(f, 'approve')} disabled={deciding}
                      data-testid={`client-gstr1-approve-${f.id}`}
                      className="text-[12px] px-2 py-1 rounded bg-emerald-500 hover:bg-emerald-400 text-black disabled:opacity-50">Approve</button>
              <button onClick={() => onGstr1Decision(f, 'request_changes')} disabled={deciding}
                      data-testid={`client-gstr1-changes-${f.id}`}
                      className="text-[12px] px-2 py-1 rounded border border-amber-500/50 text-amber-300 hover:bg-amber-500/10 disabled:opacity-50 whitespace-nowrap">Request changes</button>
            </div>
          </div>
        ) : (
          <button onClick={() => onOpenUpload(f)} disabled={busy}
            data-testid={`client-filing-upload-${f.id}`}
            className={`inline-flex items-center gap-1 text-[12px] px-2.5 py-1.5 rounded-md border transition-colors ${
              busy ? 'border-slate-700 text-slate-500' : 'border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10'}`}>
            {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Upload className="w-3 h-3" />} Upload
          </button>
        )}
      </td>
    </tr>
  );
};

const FilingTable = ({ filings, onOpenUpload, onDownload, onDownloadVersion, uploadingId, onGstr1Decision, decidingId, onNilConfirm }) => (
  <div className="overflow-x-auto rounded-lg border border-emerald-900/20">
    <table className="w-full min-w-[640px] text-sm border-collapse">
      <thead className="bg-[#080b09]">
        <tr>
          {['Return', 'Period', 'Target date', 'Documents', 'Status', ''].map((h, i) => (
            <th key={i} className="px-3 py-2.5 text-left text-[10px] uppercase tracking-wider text-slate-500 font-semibold">{h}</th>
          ))}
        </tr>
      </thead>
      <tbody>{filings.map((f) => <FilingRow key={f.id} f={f} onOpenUpload={onOpenUpload} onDownload={onDownload} onDownloadVersion={onDownloadVersion} uploadingId={uploadingId} onGstr1Decision={onGstr1Decision} decidingId={decidingId} onNilConfirm={onNilConfirm} />)}</tbody>
    </table>
  </div>
);

// QRMP: collapsed quarter cards with expandable rows
const QuarterGroups = ({ filings, regId, onOpenUpload, onDownload, onDownloadVersion, uploadingId, onGstr1Decision, decidingId, onNilConfirm }) => {
  const groups = {};
  filings.forEach((f) => {
    const { key } = quarterOf(f);
    (groups[key] = groups[key] || []).push(f);
  });
  const keys = Object.keys(groups).sort();
  const [open, setOpen] = useState(() => (keys.length ? { [keys[0]]: true } : {}));
  if (!keys.length) return <p className="text-sm text-slate-500">No filings generated yet.</p>;
  return (
    <div className="space-y-2">
      {keys.map((k) => {
        const rows = groups[k];
        const filed = rows.filter((r) => gstStatusMeta(r.status).phase === 'filing' && ['filed', 'filed_complete'].includes(gstStatusMeta(r.status).key)).length;
        const isOpen = !!open[k];
        return (
          <div key={k} className="rounded-lg border border-emerald-900/30 bg-slate-900/30">
            <button onClick={() => setOpen((o) => ({ ...o, [k]: !o[k] }))}
                    data-testid={`client-quarter-${regId}-${k.replace(/\s/g, '')}`}
                    className="w-full flex items-center justify-between p-3 text-sm hover:bg-emerald-500/5 rounded-lg">
              <span className="flex items-center gap-2 text-white font-medium">
                {isOpen ? <ChevronDown className="w-4 h-4 text-emerald-400" /> : <ChevronRight className="w-4 h-4 text-emerald-400" />}
                <CalendarClock className="w-4 h-4 text-emerald-400" /> {k}
              </span>
              <span className="text-[11px] text-slate-400">{filed}/{rows.length} filed</span>
            </button>
            {isOpen && <div className="p-3 pt-0"><FilingTable filings={rows} onOpenUpload={onOpenUpload} onDownload={onDownload} onDownloadVersion={onDownloadVersion} uploadingId={uploadingId} onGstr1Decision={onGstr1Decision} decidingId={decidingId} onNilConfirm={onNilConfirm} /></div>}
          </div>
        );
      })}
    </div>
  );
};

const InfoBit = ({ label, value }) => (
  <div>
    <p className="text-[11px] uppercase tracking-wider text-slate-500 mb-0.5">{label}</p>
    <p className="text-sm text-white">{value || <span className="text-slate-600 italic">Not set</span>}</p>
  </div>
);

const AddressLine = ({ a }) => {
  if (!a) return null;
  const parts = [a.address_line1, a.address_line2, a.city, a.pincode].filter(Boolean);
  return <p className="text-sm text-white">{parts.length ? parts.join(', ') : <span className="text-slate-600 italic">Not set</span>}</p>;
};

const AgreementCard = ({ a }) => {
  const complimentary = a.is_complimentary || a.payment_status === 'complimentary';
  return (
    <div className="rounded-lg border border-emerald-900/30 bg-[#080b09] p-4 space-y-3" data-testid={`client-agreement-${a.id}`}>
      <div className="flex items-center justify-between flex-wrap gap-2">
        <span className="text-sm text-white font-medium capitalize flex items-center gap-2">
          <IndianRupee className="w-4 h-4 text-emerald-400" />
          {String(a.pricing_type || '').replace(/_/g, ' ')} service
        </span>
        {complimentary
          ? <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-teal-500/20 text-teal-300"><Gift className="w-3 h-3" /> Complimentary</span>
          : <PayBadge status={a.payment_status} />}
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        <InfoBit label="Service period"
                 value={a.covers_period_start || a.covers_period_end
                   ? `${fmtDate(a.covers_period_start)} → ${fmtDate(a.covers_period_end)}` : null} />
        <InfoBit label="Amount" value={complimentary ? '₹0 (complimentary)' : INR(a.final_price)} />
        <InfoBit label="Covered until" value={a.paid_through_date ? fmtDate(a.paid_through_date) : null} />
      </div>
      {complimentary && a.complimentary_reason && (
        <p className="text-[11px] text-teal-300/80">Reason: {a.complimentary_reason}</p>
      )}
      {!complimentary && a.razorpay_payment_link_url &&
        !['paid', 'covered_until_date', 'paid_offline_approved'].includes(a.payment_status) && (
        <a href={a.razorpay_payment_link_url} target="_blank" rel="noopener noreferrer"
           data-testid={`client-pay-link-${a.id}`}
           className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium bg-emerald-500 hover:bg-emerald-400 text-black transition-colors">
          <ExternalLink className="w-4 h-4" /> Pay via secure link
        </a>
      )}
    </div>
  );
};

const PSTATE = {
  paid: ['Paid', 'bg-emerald-500/20 text-emerald-300 border-emerald-500/30'],
  paid_offline_approved: ['Paid', 'bg-emerald-500/20 text-emerald-300 border-emerald-500/30'],
  due: ['Payment due', 'bg-blue-500/20 text-blue-300 border-blue-500/30'],
  overdue: ['Overdue', 'bg-amber-500/20 text-amber-300 border-amber-500/30'],
  payment_hold: ['Service on hold', 'bg-red-500/20 text-red-300 border-red-500/30'],
  offline_pending_approval: ['Offline — pending approval', 'bg-purple-500/20 text-purple-300 border-purple-500/30'],
  complimentary: ['Complimentary', 'bg-indigo-500/20 text-indigo-300 border-indigo-500/30'],
};
const daysOverdue = (due) => {
  if (!due) return 0;
  const d = Math.floor((Date.now() - new Date(due).getTime()) / 86400000);
  return d > 0 ? d : 0;
};

const ClientBillingPayments = ({ recurring }) => {
  const [payingId, setPayingId] = React.useState(null);
  const payNow = React.useCallback(async (period) => {
    setPayingId(period.id);
    try {
      const res = await api.post(`/client/gst-master-agreements/${recurring.id}/periods/${period.id}/pay`)
        .then((r) => r.data);
      if (res?.short_url) {
        window.open(res.short_url, '_blank', 'noopener,noreferrer');
        toast.success('Redirecting you to the secure payment page…');
      } else {
        toast.error('Could not start payment. Please try again.');
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Payment could not be started. Please try again shortly.');
    } finally { setPayingId(null); }
  }, [recurring?.id]);

  if (!recurring || !(recurring.billing_periods || []).length) return null;
  const periods = recurring.billing_periods;
  const oldestFirst = [...periods].reverse();
  const current = oldestFirst.find((p) => p.payable)
    || oldestFirst.find((p) => p.payment_state === 'offline_pending_approval')
    || periods[0];
  const [label, cls] = PSTATE[current.payment_state] || ['—', 'bg-slate-700 text-slate-300'];
  const od = current.payment_state === 'overdue' || current.payment_state === 'payment_hold' ? daysOverdue(current.payment_due_date) : 0;

  return (
    <div data-testid="client-gst-billing">
      <h4 className="text-sm font-medium text-white mb-2 flex items-center gap-2"><IndianRupee className="w-4 h-4 text-emerald-400" /> Billing &amp; Payments <span className="text-[11px] text-slate-500 font-normal capitalize">({recurring.pricing_type})</span></h4>

      {/* Prominent current payable card */}
      {current.payment_state !== 'paid' && current.payment_state !== 'paid_offline_approved' && (
        <div className={`rounded-xl border p-4 mb-3 ${current.payment_state === 'payment_hold' ? 'border-red-500/40 bg-red-500/5' : 'border-emerald-700/40 bg-emerald-500/5'}`} data-testid="client-gst-paynow-card">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <span className={`text-[11px] px-2 py-0.5 rounded border ${cls}`} data-testid="client-gst-pay-status">{label}</span>
              <p className="text-white text-lg font-semibold mt-2">₹{Number(current.final_amount || 0).toLocaleString('en-IN')}</p>
              <p className="text-[12px] text-slate-400">Service {current.period_start} → {current.period_end}</p>
              {current.discount_value ? <p className="text-[11px] text-amber-300">Discount applied: {current.discount_value}{current.discount_type === 'percentage' ? '%' : '₹'} (was ₹{Number(current.standard_amount || 0).toLocaleString('en-IN')})</p> : null}
              <p className="text-[12px] text-slate-400 mt-1">Due {current.payment_due_date || '—'}{od > 0 ? <span className="text-amber-300"> · {od} day{od > 1 ? 's' : ''} overdue</span> : null}{current.grace_period_end ? <span className="text-slate-500"> · grace till {current.grace_period_end}</span> : null}</p>
              {current.payment_state === 'payment_hold' && <p className="text-[12px] text-red-300 mt-1.5">Service on hold due to payment. Paid-service actions (return confirmation & filing) are paused until this is cleared. Your statutory due dates are unaffected.</p>}
              {current.offline_status === 'pending_approval' && <p className="text-[12px] text-purple-300 mt-1.5">Your offline payment is submitted and pending Super Admin approval.</p>}
              {current.offline_status === 'rejected' && current.offline_rejection_remark && <p className="text-[12px] text-red-300 mt-1.5">Offline payment rejected: {current.offline_rejection_remark}. Please pay below.</p>}
            </div>
            {current.payable && (
              <div className="flex flex-col gap-2">
                <button onClick={() => payNow(current)} disabled={payingId === current.id} data-testid="client-gst-paynow-btn"
                   className="px-5 py-2 rounded-lg bg-emerald-500 hover:bg-emerald-400 disabled:opacity-60 text-black font-semibold text-sm text-center inline-flex items-center justify-center gap-2">
                  {payingId === current.id ? <><Loader2 className="w-4 h-4 animate-spin" /> Starting…</> : <>Pay Now ₹{Number(current.final_amount || 0).toLocaleString('en-IN')}</>}
                </button>
                {current.razorpay_payment_link_url && (
                  <button onClick={() => { navigator.clipboard.writeText(current.razorpay_payment_link_url); toast.success('Payment link copied'); }} data-testid="client-gst-copy-link"
                     className="px-3 py-1.5 rounded-lg border border-emerald-900/40 text-slate-300 hover:text-white text-[12px]">Copy payment link</button>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Billing history for this GSTIN */}
      <div className="overflow-x-auto rounded-lg border border-emerald-900/30">
        <table className="w-full text-[12px]">
          <thead className="bg-[#080b09] text-slate-500 uppercase tracking-wider text-[10px]">
            <tr>{['Service period', 'Amount', 'Status', 'Due', 'Invoice / Pay'].map((h, i) => <th key={i} className="px-3 py-2 text-left">{h}</th>)}</tr>
          </thead>
          <tbody>
            {periods.map((p) => {
              const [l, c] = PSTATE[p.payment_state] || ['—', 'bg-slate-700 text-slate-300'];
              return (
                <tr key={p.id} className="border-t border-emerald-900/20" data-testid={`client-gst-period-${p.id}`}>
                  <td className="px-3 py-2 text-slate-300 whitespace-nowrap">{p.period_start} → {p.period_end}</td>
                  <td className="px-3 py-2 text-emerald-300">₹{Number(p.final_amount || 0).toLocaleString('en-IN')}</td>
                  <td className="px-3 py-2"><span className={`text-[10px] px-2 py-0.5 rounded ${c}`}>{l}</span></td>
                  <td className="px-3 py-2 text-slate-400">{p.payment_due_date || '—'}</td>
                  <td className="px-3 py-2">
                    {p.invoice_id ? (
                      <a href={`/api/invoices/${p.invoice_id}/pdf?token=${localStorage.getItem('token')}`} target="_blank" rel="noopener noreferrer" data-testid={`client-gst-invoice-${p.id}`} className="text-emerald-300 hover:text-white">Download</a>
                    ) : p.payable ? (
                      <button onClick={() => payNow(p)} disabled={payingId === p.id} data-testid={`client-gst-pay-${p.id}`} className="text-emerald-400 hover:text-white font-medium disabled:opacity-60 inline-flex items-center gap-1">
                        {payingId === p.id ? <><Loader2 className="w-3 h-3 animate-spin" /> Starting…</> : 'Pay now →'}
                      </button>
                    ) : '—'}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};


const RegistrationCard = ({ reg, onRequestUpdate, requesting, onOpenUpload, onDownload, onDownloadVersion, uploadingId, onGstr1Decision, decidingId, onNilConfirm }) => {
  const qrmp = isQrmpReg(reg);
  const [reqOpen, setReqOpen] = React.useState(false);
  const [reqFreq, setReqFreq] = React.useState(qrmp ? 'monthly' : 'quarterly');
  const [reqNote, setReqNote] = React.useState('');
  const [reqBusy, setReqBusy] = React.useState(false);
  const submitFreqRequest = async () => {
    setReqBusy(true);
    try {
      await api.post(`/client/gst-registrations/${reg.id}/request-scheme-change`, { requested_frequency: reqFreq, note: reqNote.trim() || undefined });
      toast.success('Request sent to your CA for review');
      setReqOpen(false); setReqNote('');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to send request'); }
    finally { setReqBusy(false); }
  };
  const fyOptions = React.useMemo(
    () => [...new Set((reg.filings || []).map((f) => f.financial_year).filter(Boolean))].sort().reverse(),
    [reg.filings]);
  const [fy, setFy] = React.useState('');
  const activeFy = fy || fyOptions[0] || '';
  const visibleFilings = React.useMemo(
    () => (reg.filings || []).filter((f) => !activeFy || f.financial_year === activeFy),
    [reg.filings, activeFy]);
  return (
    <div className="space-y-5" data-testid={`client-gst-reg-${reg.id}`}>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 items-start">
          <div className="bg-[#0f1611] border border-emerald-900/30 rounded-xl p-5 space-y-4">
            {/* header */}
            <div className="space-y-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-mono font-bold tracking-wider text-emerald-300 text-sm">{reg.gstin}</span>
                <span className="text-slate-600">·</span>
                <span className="text-slate-300 text-sm">{reg.state}</span>
                <VerificationBadge status={reg.verification_status} />
              </div>
              <p className="text-white font-medium truncate">{reg.trade_name || reg.legal_name}</p>
              {reg.trade_name && reg.legal_name && reg.trade_name !== reg.legal_name && (
                <p className="text-[12px] text-slate-500 truncate">Legal: {reg.legal_name}</p>
              )}
            </div>

            {/* scheme + frequency change */}
            <div className="flex items-center justify-between gap-3 rounded-lg border border-emerald-900/20 bg-[#080b09]/60 p-3">
              <div>
                <p className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">Filing scheme</p>
                <p className="text-sm text-white mt-0.5">{qrmp ? 'QRMP (Quarterly)' : 'Monthly'}</p>
              </div>
              <button onClick={() => setReqOpen((v) => !v)} data-testid={`client-request-freq-${reg.id}`}
                      className="text-[11px] text-emerald-400 hover:text-emerald-300 flex items-center gap-1 whitespace-nowrap">
                <RefreshCw className="w-3 h-3" /> Change
              </button>
            </div>

            {reqOpen && (
              <div className="rounded-lg border border-emerald-700/40 bg-emerald-500/5 p-3 space-y-2" data-testid={`client-freq-request-form-${reg.id}`}>
                <p className="text-[12px] text-slate-300">Ask your CA to change how often this GSTIN files returns. They'll review and apply it.</p>
                <div className="flex flex-wrap items-center gap-2">
                  <select value={reqFreq} onChange={(e) => setReqFreq(e.target.value)} data-testid={`client-freq-select-${reg.id}`}
                          className="bg-[#0b0f0c] border border-emerald-900/40 rounded-lg text-slate-200 text-[12px] px-2.5 py-1.5 focus:outline-none focus:border-emerald-500">
                    <option value="monthly">Monthly</option>
                    <option value="quarterly">Quarterly</option>
                  </select>
                  <input value={reqNote} onChange={(e) => setReqNote(e.target.value)} placeholder="Optional note for your CA" data-testid={`client-freq-note-${reg.id}`}
                         className="flex-1 min-w-[140px] bg-[#0b0f0c] border border-emerald-900/40 rounded-lg text-slate-200 text-[12px] px-2.5 py-1.5 focus:outline-none focus:border-emerald-500" />
                  <button onClick={submitFreqRequest} disabled={reqBusy} data-testid={`client-freq-submit-${reg.id}`}
                          className="px-3 py-1.5 rounded-lg bg-emerald-500 hover:bg-emerald-400 text-black text-[12px] font-medium disabled:opacity-50 flex items-center gap-1">
                    {reqBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />} Send request
                  </button>
                </div>
              </div>
            )}

            {/* scheme summary */}
            <div className="grid grid-cols-2 gap-2.5">
              <InfoBit label="Taxpayer profile" value={String(reg.taxpayer_profile || '').replace(/_/g, ' ')} />
              <InfoBit label="GSTR-1" value={SCHEME_LABEL[reg.gstr1_return_type] || '—'} />
              <InfoBit label="GSTR-3B mode" value={reg.gstr3b_payment_mode === 'pmt06_qrmp' ? 'PMT-06 (QRMP)' : reg.gstr3b_payment_mode === 'regular' ? 'Regular' : '—'} />
              <InfoBit label="Filing since" value={reg.filing_start_month} />
            </div>
          </div>

          {/* Billing & Payments (recurring) */}
          {(reg.recurring_billing?.billing_periods || []).length > 0 && (
            <div className="bg-gradient-to-b from-[#0f1611] to-[#0b0f0c] border border-emerald-900/40 rounded-xl p-5 shadow-md">
              <ClientBillingPayments recurring={reg.recurring_billing} />
            </div>
          )}

          {/* CA service / payment */}
          <div className="bg-[#0f1611] border border-emerald-900/30 rounded-xl p-5">
            <h4 className="text-sm font-medium text-white mb-3 flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-400" /> CA service &amp; payment</h4>
            {reg.agreements.length === 0
              ? <p className="text-sm text-slate-500">No service agreement on record yet. Your CA will set this up.</p>
              : <div className="space-y-2">{reg.agreements.map((a) => <AgreementCard key={a.id} a={a} />)}</div>}
            <p className="text-[11px] text-slate-500 mt-3 flex items-start gap-1.5">
              <IndianRupee className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
              Annual returns (GSTR-9 / GSTR-9C) are billed separately as distinct services and fees.
            </p>
          </div>
      </div>

      {/* ── Filing summary — full width ── */}
      <div className="bg-[#0f1611] border border-emerald-900/30 rounded-xl p-5">
            <div className="flex items-center justify-between mb-3 flex-wrap gap-2 pb-3 border-b border-emerald-900/30">
              <h4 className="text-sm font-medium text-white flex items-center gap-2"><FileText className="w-4 h-4 text-emerald-400" /> Filing summary</h4>
              {fyOptions.length > 0 && (
                <select value={activeFy} onChange={(e) => setFy(e.target.value)} data-testid={`client-gst-fy-filter-${reg.id}`}
                  className="bg-[#0b0f0c] border border-emerald-900/40 rounded-lg text-slate-200 text-[12px] px-2.5 py-1.5 focus:outline-none focus:border-emerald-500">
                  {fyOptions.map((y) => <option key={y} value={y}>FY {y}</option>)}
                </select>
              )}
            </div>
            {(reg.filings || []).length === 0
              ? <p className="text-sm text-slate-500">No filings generated yet.</p>
              : visibleFilings.length === 0
                ? <p className="text-sm text-slate-500">No filings for FY {activeFy}.</p>
                : qrmp
                  ? <QuarterGroups filings={visibleFilings} regId={reg.id} onOpenUpload={onOpenUpload} onDownload={onDownload} onDownloadVersion={onDownloadVersion} uploadingId={uploadingId} onGstr1Decision={onGstr1Decision} decidingId={decidingId} onNilConfirm={onNilConfirm} />
                  : <FilingTable filings={visibleFilings} onOpenUpload={onOpenUpload} onDownload={onDownload} onDownloadVersion={onDownloadVersion} uploadingId={uploadingId} onGstr1Decision={onGstr1Decision} decidingId={decidingId} onNilConfirm={onNilConfirm} />}
          </div>

          {/* read-only profile */}
          <div className="bg-[#0f1611] border border-emerald-900/30 rounded-xl p-5">
            <h4 className="text-sm font-medium text-white mb-3 flex items-center gap-2"><Building2 className="w-4 h-4 text-emerald-400" /> GST profile <span className="text-[11px] text-slate-500 font-normal">(read-only)</span></h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 rounded-lg border border-emerald-900/20 p-3 bg-[#080b09]/60">
              <InfoBit label="Trade name" value={reg.trade_name} />
              <InfoBit label="Constitution" value={reg.constitution_of_business} />
              <InfoBit label="Annual turnover" value={reg.annual_turnover_range} />
              <InfoBit label="Nature of business" value={Array.isArray(reg.nature_of_business) ? reg.nature_of_business.join(', ') : null} />
              <div className="sm:col-span-2">
                <p className="text-[11px] uppercase tracking-wider text-slate-500 mb-0.5 flex items-center gap-1"><MapPin className="w-3 h-3" /> Principal place of business</p>
                <AddressLine a={reg.principal_place_of_business} />
              </div>
              {Array.isArray(reg.additional_places_of_business) && reg.additional_places_of_business.length > 0 && (
                <div className="sm:col-span-2">
                  <p className="text-[11px] uppercase tracking-wider text-slate-500 mb-0.5">Additional places</p>
                  {reg.additional_places_of_business.map((a, i) => <AddressLine key={i} a={a} />)}
                </div>
              )}
              {reg.client_visible_notes && (
                <div className="sm:col-span-2">
                  <p className="text-[11px] uppercase tracking-wider text-slate-500 mb-0.5">Notes from your CA</p>
                  <p className="text-sm text-white whitespace-pre-wrap">{reg.client_visible_notes}</p>
                </div>
              )}
            </div>
            <div className="mt-3 flex items-center justify-between flex-wrap gap-2">
              <p className="text-[11px] text-slate-500">GSTIN, filing scheme and other controlled fields are managed by your CA.</p>
              <button onClick={() => onRequestUpdate(reg.id)} disabled={requesting === reg.id}
                      data-testid={`client-request-update-${reg.id}`}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium border border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10 disabled:opacity-50 transition-colors">
                {requesting === reg.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                Request profile update
              </button>
            </div>
          </div>
    </div>
  );
};

const ClientUploadModal = ({ filing, onClose, onUpload }) => {
  const [checklist, setChecklist] = useState(null);
  const [docType, setDocType] = useState('');
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);

  React.useEffect(() => {
    let alive = true;
    api.get(`/client/gst-filings/${filing.id}/checklist`)
      .then((r) => { if (!alive) return; const cl = r.data.checklist || []; setChecklist(cl); setDocType(cl[0]?.code || 'other'); })
      .catch(() => { if (alive) { setChecklist([]); setDocType('other'); } });
    return () => { alive = false; };
  }, [filing.id]);

  const options = React.useMemo(() => {
    const cl = checklist || [];
    const seen = new Set(cl.map((c) => c.code));
    const opts = cl.map((c) => ({ code: c.code, label: c.label + (c.required ? ' *' : '') + (c.requested_later ? ' (requested)' : '') }));
    if (!seen.has('other')) opts.push({ code: 'other', label: 'Other supporting documents' });
    return opts;
  }, [checklist]);

  const submit = async () => {
    if (!files.length) { toast.error('Choose at least one file'); return; }
    setBusy(true);
    try { await onUpload(files, docType || 'other'); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" data-testid="client-upload-modal" onClick={onClose}>
      <div className="w-full max-w-md rounded-xl border border-emerald-900/40 bg-[#0f1611] p-5" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-white font-medium flex items-center gap-2"><Upload className="w-4 h-4 text-emerald-400" /> Upload documents</h3>
          <button onClick={onClose} data-testid="client-upload-close" className="text-slate-400 hover:text-white"><X className="w-4 h-4" /></button>
        </div>
        <p className="text-[12px] text-slate-400 mb-3">{filing.form_type} · {filing.filing_period}. Tag your files so your CA can match them to the checklist.</p>

        <label className="text-[11px] uppercase tracking-wider text-slate-500 block mb-1">Document type</label>
        {checklist === null
          ? <p className="text-[12px] text-slate-500 mb-3">Loading checklist…</p>
          : (
            <select value={docType} onChange={(e) => setDocType(e.target.value)} data-testid="client-upload-doctype"
              className="w-full bg-[#0b0f0c] border border-emerald-900/40 rounded-lg text-slate-200 text-sm px-3 py-2 mb-3 focus:outline-none focus:border-emerald-500">
              {options.map((o) => <option key={o.code} value={o.code}>{o.label}</option>)}
            </select>
          )}

        <label className="text-[11px] uppercase tracking-wider text-slate-500 block mb-1">Files</label>
        <input type="file" multiple accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.xls,.xlsx" data-testid="client-upload-files"
          onChange={(e) => setFiles(Array.from(e.target.files || []))}
          className="w-full text-[12px] text-slate-300 file:mr-3 file:py-1.5 file:px-3 file:rounded-md file:border-0 file:bg-emerald-500/20 file:text-emerald-300 file:text-[12px] mb-2" />
        {files.length > 0 && <p className="text-[11px] text-slate-400 mb-3">{files.length} file(s) selected</p>}

        <div className="flex justify-end gap-2 pt-2 border-t border-emerald-900/30 mt-2">
          <button onClick={onClose} className="text-sm text-slate-300 hover:text-white">Cancel</button>
          <button onClick={submit} disabled={busy || !files.length} data-testid="client-upload-submit"
            className="px-4 py-1.5 rounded-lg text-sm font-medium bg-emerald-500 hover:bg-emerald-400 text-black disabled:opacity-50 flex items-center gap-1">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} Upload
          </button>
        </div>
      </div>
    </div>
  );
};

const ClientGstTab = ({ companyId, registrations = [], onReload }) => {
  const [requesting, setRequesting] = useState(null);
  const [uploadingId, setUploadingId] = useState(null);
  const [decidingId, setDecidingId] = useState(null);
  const [uploadFiling, setUploadFiling] = useState(null);

  const gstr1Decision = async (filing, decision) => {
    let remark = '';
    if (decision === 'request_changes') {
      remark = window.prompt('Describe the changes required (min 3 characters):') || '';
      if (remark.trim().length < 3) { toast.error('A remark of at least 3 characters is required'); return; }
    }
    setDecidingId(filing.id);
    try {
      const res = await api.post(`/client/gst-filings/${filing.id}/gstr1-decision`,
        { decision, remark: remark || null }).then((x) => x.data);
      toast.success(res.message);
      onReload?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Action failed');
    } finally { setDecidingId(null); }
  };

  const confirmNil = async (filing, selected) => {
    if (!selected) {
      const ok = window.confirm('This tells your CA you had transactions this period and removes the NIL marking. Continue?');
      if (!ok) return;
    }
    setDecidingId(filing.id);
    try {
      await api.post(`/client/gst-filings/${filing.id}/toggle-nil`, { selected });
      toast.success(selected ? 'NIL confirmed — your CA will file the return.' : 'Your CA has been notified.');
      onReload?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Action failed');
    } finally { setDecidingId(null); }
  };

  const requestUpdate = async (regId) => {
    setRequesting(regId);
    try {
      const res = await api.post(`/client/gst-registrations/${regId}/profile-update-request`).then((x) => x.data);
      toast.success(res.message || 'Request sent to your CA team');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to send request');
    } finally { setRequesting(null); }
  };

  const uploadDoc = async (filingId, files, documentType = 'other') => {
    const list = Array.from(files || []);
    if (!list.length) return;
    setUploadingId(filingId);
    try {
      const fd = new FormData();
      list.forEach((f) => fd.append('files', f));
      fd.append('document_type', documentType || 'other');
      const res = await api.post(`/client/gst-filings/${filingId}/documents/bulk`, fd,
        { headers: { 'Content-Type': 'multipart/form-data' } }).then((x) => x.data);
      const dups = (res.duplicates || []).length;
      toast.success(`${res.uploaded} file(s) uploaded${dups ? `, ${dups} duplicate skipped` : ''}`);
      onReload?.();
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Upload failed';
      if (e?.response?.status === 402) toast.error(msg, { duration: 6000 });
      else toast.error(msg);
    } finally { setUploadingId(null); }
  };

  const downloadDoc = async (filingId, doc) => {
    try {
      const res = await api.get(`/client/gst-filings/${filingId}/documents/${doc.id}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: doc.mime_type || 'application/octet-stream' }));
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (e) {
      toast.error('Could not open document');
    }
  };

  const downloadGstr1Version = async (filingId, version) => {
    try {
      const res = await api.get(`/client/gst-filings/${filingId}/gstr1-version/${version.document_id}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: version.mime_type || 'application/octet-stream' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = version.file_name || version.document_name || `GSTR-1_V${version.version_number}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => window.URL.revokeObjectURL(url), 4000);
    } catch (e) {
      toast.error('Could not download the GSTR-1 worksheet');
    }
  };

  if (registrations.length === 0) {
    return (
      <div className="text-center py-14 border border-dashed border-emerald-900/30 rounded-xl" data-testid="client-gst-empty">
        <ShieldCheck className="w-8 h-8 text-emerald-500/50 mx-auto mb-3" />
        <p className="text-sm text-slate-400">No GST registrations on record for this company yet.</p>
      </div>
    );
  }
  return (
    <div className="space-y-6" data-testid="client-gst-tab">
      {registrations.map((reg) => (
        <RegistrationCard key={reg.id} reg={reg} onRequestUpdate={requestUpdate} requesting={requesting}
                          onOpenUpload={setUploadFiling} onDownload={downloadDoc} onDownloadVersion={downloadGstr1Version} uploadingId={uploadingId}
                          onGstr1Decision={gstr1Decision} decidingId={decidingId} onNilConfirm={confirmNil} />
      ))}
      {uploadFiling && (
        <ClientUploadModal
          filing={uploadFiling}
          onClose={() => setUploadFiling(null)}
          onUpload={async (files, docType) => { await uploadDoc(uploadFiling.id, files, docType); setUploadFiling(null); }}
        />
      )}
    </div>
  );
};

export default ClientGstTab;
